import React, { useEffect, useRef, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { Colors, Spacing } from '_styles';
import { SESSION_MAX_DURATION_MS, SESSION_HEARTBEAT_MS, AIRPORTS_SUPPORTS_SESSIONS, TIMELINE_V2_AIRPORTS } from '_utils/constants';

// navigation
import { useHistory } from "react-router-dom";
import { NavLink } from "react-router-dom";

// sidebar library
import { CDBSidebar, CDBSidebarHeader, CDBSidebarMenuItem, CDBSidebarContent, CDBSidebarMenu, CDBSidebarFooter } from "cdbreact";

// icons
import { SidebarIcon } from "_molecules";
import { FiLogOut } from "react-icons/fi";
import logo from "_assets/images/logo/white-logo-text.png";

//redux
import { useSelector, useDispatch } from "react-redux";
import {
  logout,
  getDbInfo,
  selectFirebaseUser,
  selectUserDb,
  reset,
  resetStatus,
  selectTravelAgent,
} from "_redux/slices/main-screen/user"
import { onSnapshot, collection, query, where } from 'firebase/firestore';
import { firestore } from '_utils/firebase-app';


import { reset as HistoryReset } from "_redux/slices/assistance-bookings/bookings-history";
import { reset as TableReset } from "_redux/slices/prm-bookings/bookings-table";
import { reset as BookingsReset } from "_redux/slices/prm-bookings/bookings";
import { reset as CalendarReset } from "_redux/slices/prm-bookings/calendar";
import { reset as ScreenReset } from "_redux/slices/main-screen/render-screen";
import { reset as ReviewsReset } from "_redux/slices/analytics/reviews";
import { reset as RewardsReset } from "_redux/slices/prm-club/rewards";
import { reset as UserBookingsReset } from "_redux/slices/website/userbookings";
import { reset as resetWeather } from "_redux/slices/analytics/weather";
//timeline reducers
import { reset as EmployeeReset } from '_redux/slices/timeline/employees';
import { reset as FlightScannerReset } from '_redux/slices/timeline/flight-scanner';
import { reset as SelectedTaskReset } from '_redux/slices/timeline/selected-task';
import { reset as TimelineAreaReset } from '_redux/slices/timeline/timeline-areas';
import { reset as TimelineDataReset } from '_redux/slices/timeline/timeline-data';
import { reset as resetTimeLineControl } from "_redux/slices/timeline/timeline-control";
import { reset as resetTimeLineDashboard } from "_redux/slices/timeline/timeline-dashboard";
import { getAllSupportDesks, selectUnreadChatsCount, setUnreadChat, readAllChats } from "_redux/slices/messaging/messaging";
import { closePortalSession, openPortalSession, sendSessionHeartbeat, getRemainingSessionMs } from '_services/session-service';


// Incident statuses that still need attention: new (open) + acknowledged-pending-approval.
// 'closed' means approved & complete, so it is excluded from the sidebar badge count.
const INCIDENT_NEEDS_ACTION_STATUSES = ['open', 'in_progress', 'fully_acknowledged'];

const Sidebar = (props) => {

  //#region redux
  const dispatch = useDispatch();
  const firebaseUser = useSelector(selectFirebaseUser);
  const travelAgent = useSelector(selectTravelAgent);
  const userDb = useSelector(selectUserDb);
  const unreadChatsCount = useSelector(selectUnreadChatsCount)
  //#endregion

  // Count of incident reports awaiting acknowledgement / approval, shown as a sidebar badge.
  const [incidentActionCount, setIncidentActionCount] = useState(0);

  const bookingsFeedbackOnly = userDb?.role === "BOOKINGS_FEEDBACK";
  const securityOnly = userDb?.role === "SECURITY";

  // states

  const [retailer, setRetailer] = useState(false)
  const [showTimeoutModal, setShowTimeoutModal] = useState(false)
  const sessionTimeoutRef = useRef(null)

  // nav
  const history = useHistory();

  // handle icon press
  const handlePress = (page) => {
    history.push("/" + page);
  };

  // logout
  const handleLogout = async (closedBy = "user") => {
    // logout and change page
    await closePortalSession(closedBy);
    dispatch(logout());
    history.push("/login");

    // reset all slices
    dispatch(resetStatus());
    dispatch(reset());
    dispatch(HistoryReset());
    dispatch(TableReset());
    dispatch(BookingsReset());
    dispatch(CalendarReset());
    dispatch(ScreenReset());
    dispatch(ReviewsReset());
    dispatch(RewardsReset());
    dispatch(UserBookingsReset());
    dispatch(resetWeather());
    dispatch(EmployeeReset());
    dispatch(FlightScannerReset());
    dispatch(SelectedTaskReset());
    dispatch(TimelineAreaReset());
    dispatch(TimelineDataReset());
    dispatch(resetTimeLineControl());
    dispatch(resetTimeLineDashboard());
    dispatch(readAllChats())
  };


  const handleTimeoutCase = () => {
    setShowTimeoutModal(true);
    handleLogout('timeout');
  }

  useEffect(() => {
    dispatch(readAllChats())
  }, [])

  useEffect(() => {
    if (!userDb?.details?.authority) return;
    const iata = userDb.details.authority;

    const q = query(
      collection(firestore, 'conversations'),
      where('portalUser', '==', iata)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      let hasUnreadCount = 0;

      snapshot.docChanges().forEach((change) => {
        const data = change.doc.data();

        if (data.notification === true && !data.archived && data.lastAction != undefined) {
          hasUnreadCount += 1;
        }

      });

      if (hasUnreadCount) {
        dispatch(setUnreadChat(hasUnreadCount));
      } else {
        dispatch(setUnreadChat(0)); // or reset when all are read
      }
    });

    return () => unsubscribe();
  }, [userDb]);

  // Live count of incident reports still needing action (new + pending approval).
  // Filtered client-side by status to avoid a composite (iata + status) index.
  useEffect(() => {
    if (!userDb?.details?.authority) return;
    const iata = userDb.details.authority;

    const q = query(
      collection(firestore, 'incidentReports'),
      where('iata', '==', iata)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      let count = 0;
      snapshot.forEach((doc) => {
        if (INCIDENT_NEEDS_ACTION_STATUSES.includes(doc.data().status)) count += 1;
      });
      setIncidentActionCount(count);
    });

    return () => unsubscribe();
  }, [userDb]);


  useEffect(() => {
    const interval = setInterval(async () => {
      const result = await sendSessionHeartbeat();
      if (result?.success === false && result?.reason === 'already_closed') {
        clearInterval(interval);
        handleTimeoutCase();
      }
    }, SESSION_HEARTBEAT_MS);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Absolute session timeout: force logout 12h 30m after login regardless of activity.
  // Survives page refresh by reading the stored login timestamp.
  useEffect(() => {
    if (!AIRPORTS_SUPPORTS_SESSIONS.includes(userDb?.details?.authority)) return;
    const remaining = getRemainingSessionMs(SESSION_MAX_DURATION_MS);
    if (remaining === null) return;
    if (remaining <= 0) {
      handleTimeoutCase();
      return;
    }
    clearTimeout(sessionTimeoutRef.current);
    sessionTimeoutRef.current = setTimeout(handleTimeoutCase, remaining);
    return () => clearTimeout(sessionTimeoutRef.current);
  }, [userDb]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (userDb == null || (userDb == undefined && firebaseUser)) {
      dispatch(getDbInfo(firebaseUser.uid));
      getAllSupportDesks();
    }
    if (userDb && userDb.details && userDb.details.type == 'RETAILER') {
      setRetailer(true)
    }
    if (userDb && firebaseUser && AIRPORTS_SUPPORTS_SESSIONS.includes(userDb.details?.authority)) {
      const body = {
        userId: firebaseUser.uid,
        email: firebaseUser.email,
        role: userDb.role || userDb.position || "Not Provided",
        airport: userDb.details.authority,
        title: firebaseUser.displayName || "Not Provided",
        type: 'portal'
      }
      openPortalSession(body);
    }
  }, [userDb]);

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'scroll initial', position: '-webkit-sticky', position: 'sticky', top: 0 }}>
      <CDBSidebar textColor="#fff" backgroundColor={Colors.SIDEBAR_COLOR} toggled={true}>

        {/* Header */}
        <CDBSidebarHeader
          prefix={<i style={{ marginTop: Spacing.SCALE_8 }} className="fa fa-bars" />}
        >
          <a
            href="/calendar"
            className="text-decoration-none"
            style={{ color: "inherit" }}
          >
            <img src={logo} style={{ width: "90%", height: "auto" }} />
          </a>
        </CDBSidebarHeader>

        {/* Content */}
        <CDBSidebarContent className="sidebar-content">
          <CDBSidebarMenu>
            {securityOnly ? (
              <>
                <NavLink
                  onClick={() => {
                    handlePress("prm-bookings");
                  }}
                  exact
                  to="/prm-bookings"
                  activeClassName="activeClicked"
                >
                  <CDBSidebarMenuItem icon="list">Bookings</CDBSidebarMenuItem>
                </NavLink>
                <NavLink
                  onClick={() => {
                    handlePress("account");
                  }}
                  exact
                  to="/account"
                  activeClassName="activeClicked"
                >
                  <CDBSidebarMenuItem icon="user">Account</CDBSidebarMenuItem>
                </NavLink>
              </>
            ) : bookingsFeedbackOnly ? (
              <>
                <NavLink exact to="/prm-calendar" activeClassName="activeClicked">
                  <CDBSidebarMenuItem icon="calendar">Calendar</CDBSidebarMenuItem>
                </NavLink>
                <NavLink
                  onClick={() => {
                    handlePress("create-booking");
                  }}
                  exact
                  to="/create-booking"
                  activeClassName="activeClicked"
                >
                  <CDBSidebarMenuItem icon="plus">Create Booking</CDBSidebarMenuItem>
                </NavLink>
                <NavLink
                  onClick={() => {
                    handlePress("feedback");
                  }}
                  exact
                  to="/feedback"
                  activeClassName="activeClicked"
                >
                  <CDBSidebarMenuItem icon="envelope">Feedback</CDBSidebarMenuItem>
                </NavLink>
                <NavLink
                  onClick={() => {
                    handlePress("account");
                  }}
                  exact
                  to="/account"
                  activeClassName="activeClicked"
                >
                  <CDBSidebarMenuItem icon="user">Account</CDBSidebarMenuItem>
                </NavLink>
              </>
            ) : (
              <>
                {/* Calendar */}
                <NavLink exact to="/prm-calendar" activeClassName="activeClicked">
                  {userDb &&
                    userDb != null &&
                    userDb != undefined &&
                    userDb.role &&
                    userDb.role != undefined && !(userDb.details.type === "AIRLINE" && userDb.details.authority === "ALL") ? (
                    <CDBSidebarMenuItem icon="calendar">
                      Calendar
                    </CDBSidebarMenuItem>
                  ) : (
                    ""
                  )}
                </NavLink>

                {/* Assistance Bookings */}
                <NavLink
                  onClick={() => {
                    handlePress("assistance-bookings");
                  }}
                  exact
                  to="/assistance-bookings"
                  activeClassName="activeClicked"
                >
                  <CDBSidebarMenuItem icon="plus">Bookings</CDBSidebarMenuItem>
                </NavLink>

                <NavLink onClick={() => { handlePress(TIMELINE_V2_AIRPORTS.includes(userDb.details.authority) ? 'timeline-v2' : 'timeline') }} exact to={TIMELINE_V2_AIRPORTS.includes(userDb.details.authority) ? "/timeline-v2" : "/timeline"} activeClassName="activeClicked">
                  {userDb && userDb != null && userDb != undefined && userDb.role && userDb.role != undefined && userDb.details.type === "AIRPORT" &&
                    <CDBSidebarMenuItem icon="clock">Timeline</CDBSidebarMenuItem>
                  }
                </NavLink>

                {/* Feedback */}
                <NavLink
                  onClick={() => {
                    handlePress("feedback");
                  }}
                  exact
                  to="/feedback"
                  activeClassName="activeClicked"
                >
                  {travelAgent || retailer || (userDb.details.type === "AIRLINE" && userDb.details.authority === "ALL") ? (
                    ""
                  ) : (
                    <CDBSidebarMenuItem icon="envelope">
                      Feedback
                    </CDBSidebarMenuItem>
                  )}
                </NavLink>

                {/* Support Desk */}
                <NavLink
                  onClick={() => {
                    handlePress("support-desk");
                  }}
                  exact
                  to="/support-desk"
                  activeClassName="activeClicked"
                >
                  {userDb &&
                    userDb != undefined &&
                    userDb != null &&
                    userDb.details &&
                    userDb.details != undefined &&
                    userDb.details.authority &&
                    userDb.details.authority != undefined && (
                      <CDBSidebarMenuItem icon="desktop">
                        <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          Support Desk
                          {unreadChatsCount > 0 && (
                            <span
                              style={{
                                backgroundColor: "red",
                                borderRadius: "30%",
                                color: "white",
                                padding: "2px 6px",
                                fontSize: "16px",
                                marginLeft: "4px",
                                minWidth: "18px",
                                textAlign: "center",
                                display: "inline-block",
                              }}
                            >
                              {unreadChatsCount}
                            </span>
                          )}
                        </span>
                      </CDBSidebarMenuItem>
                    )}
                </NavLink>

                {/* Incident Reports  */}
                <NavLink
                  onClick={() => { handlePress('incident-reports'); }}
                  exact
                  to="/incident-reports"
                  activeClassName="activeClicked"
                >
                  {userDb &&
                    userDb != undefined &&
                    userDb != null &&
                    userDb.details &&
                    userDb.details.authority &&
                    userDb.details.authority != undefined && (
                      <CDBSidebarMenuItem icon="flag">
                        <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          Incident Reports
                          {incidentActionCount > 0 && (
                            <span
                              style={{
                                backgroundColor: "red",
                                borderRadius: "30%",
                                color: "white",
                                padding: "2px 6px",
                                fontSize: "16px",
                                marginLeft: "4px",
                                minWidth: "18px",
                                textAlign: "center",
                                display: "inline-block",
                              }}
                            >
                              {incidentActionCount}
                            </span>
                          )}
                        </span>
                      </CDBSidebarMenuItem>
                    )}
                </NavLink>


                {/* Archive */}
                <NavLink
                  onClick={() => { handlePress('archive'); }}
                  exact
                  to="/archive"
                  activeClassName="activeClicked"
                >
                  {userDb &&
                    userDb != undefined &&
                    userDb != null &&
                    userDb.details &&
                    userDb.details.authority &&
                    userDb.details.authority != undefined && (
                      <CDBSidebarMenuItem icon="archive">
                        Archive
                      </CDBSidebarMenuItem>
                    )}
                </NavLink>

                {/* Global SLA Card */}
                {
                  <NavLink
                    onClick={() => {
                      handlePress("global-sla");
                    }}
                    exact
                    to="/global-sla"
                    activeClassName="activeClicked"
                  >
                    {travelAgent || retailer ||
                      (userDb &&
                        userDb != undefined &&
                        userDb != null &&
                        userDb.details &&
                        userDb.details != undefined &&
                        userDb.details.type &&
                        userDb.details.type != undefined &&
                        userDb.details.type == "AIRLINE") ? (
                      ""
                    ) : (
                      <CDBSidebarMenuItem icon="globe">Airports</CDBSidebarMenuItem>
                    )}
                  </NavLink>
                }

                {/* Marketplace */}
                {/* <NavLink onClick={()=>{handlePress('support-desk')}} exact to="/support-desk" activeClassName="activeClicked">
              <CDBSidebarMenuItem icon="desktop">Support Desk</CDBSidebarMenuItem>
            </NavLink> */}

                {/* Marketplace */}
                {/* Temp hardcoded condition to display marketplace only for Sarah */}
                <NavLink
                  onClick={() => {
                    handlePress("marketplace");
                  }}
                  exact
                  to="/marketplace"
                  activeClassName="activeClicked"
                >
                  {firebaseUser &&

                    firebaseUser.email === "sarah.mulreany@prmassist.com" ? (
                    <CDBSidebarMenuItem icon="shopping-cart">
                      Marketplace
                    </CDBSidebarMenuItem>
                  ) : (
                    ""
                  )}
                  {/* {userDb &&

                    userDb.role !== "GARETH" ? (
                    <CDBSidebarMenuItem icon="shopping-cart">
                      Marketplace
                    </CDBSidebarMenuItem>
                  ) : (
                    ""
                  )} */}
                </NavLink>

                {/* Account */}
                <a
                  href={"https://connect.prmassist.com/"}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {!retailer && <CDBSidebarMenuItem icon="book">Connect</CDBSidebarMenuItem>}
                </a>

                <NavLink onClick={() => { handlePress('total-reports') }} exact to="/total-reports" activeClassName="activeClicked">
                  {userDb && userDb != null && userDb != undefined && userDb.role && userDb.role != undefined && (userDb.details.type === "AIRPORT" || (userDb.details.type === "AIRLINE" && userDb.details.authority === "ALL")) &&
                    <CDBSidebarMenuItem icon="file">Reports</CDBSidebarMenuItem>
                  }
                </NavLink>

                {/* Account */}
                <NavLink
                  onClick={() => {
                    handlePress("account");
                  }}
                  exact
                  to="/account"
                  activeClassName="activeClicked"
                >
                  <CDBSidebarMenuItem icon="user">Account</CDBSidebarMenuItem>
                </NavLink>

                {/* Updates */}
                <NavLink
                  onClick={() => {
                    handlePress("notifications");
                  }}
                  exact
                  to="/notifications"
                  activeClassName="activeClicked"
                >
                  {!retailer && <CDBSidebarMenuItem icon="bell">Update Hub</CDBSidebarMenuItem>}
                </NavLink>
                <NavLink
                  onClick={() => {
                    window.open("https://prmassist.ispring.eu/");
                  }}
                  exact
                  to="/training"
                  activeClassName="training"
                >
                  {!retailer && <CDBSidebarMenuItem icon="bell">Training</CDBSidebarMenuItem>}
                </NavLink>
              </>
            )}
          </CDBSidebarMenu>
        </CDBSidebarContent>



        {/* Absolute session timeout modal */}
        <Modal show={showTimeoutModal} centered onHide={() => setShowTimeoutModal(false)} backdrop="static">
          <Modal.Header>
            <Modal.Title style={{ fontWeight: 'bold' }}>Session Expired</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p style={{ margin: 0 }}>
              Your session has reached the maximum duration of 12 hours 30 minutes.
              For your security, you have been automatically logged out.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={() => setShowTimeoutModal(false)}>OK</Button>
          </Modal.Footer>
        </Modal>

        {/* Footer */}
        <CDBSidebarFooter
          style={{ textAlign: "center" }}
          prefix={<i className="fa fa-bars fa-large"></i>}
        >
          <SidebarIcon
            icon={
              <FiLogOut
                style={{
                  marginTop: Spacing.SCALE_12,
                  textAlign: "center",
                  justifySelf: "center",
                }}
                size={32}
                color={"white"}
              />
            }
            handleClick={() => handleLogout()}
          />
        </CDBSidebarFooter>
      </CDBSidebar>
    </div>
  );
}



export default Sidebar;